import React, { useMemo, useState, useEffect } from "react";
import { useTable, useSortBy, usePagination, useRowSelect } from "react-table";
import { Empty, message } from "antd";
import "../../../styles.css";
import Vectorsorting from "../../../assets/Vectorsorting.svg";
import ShimmerLoader from "../../../loading";
import { useTranslation } from "react-i18next";
import CustomPagination from "../../../components/CustomWidgets/CustomPagination";
import { EditOutlined, DeleteOutlined } from "@ant-design/icons"; // Import icons
import {
  activateSubStore,
  deactivateSubStore,
  deleteSubStore,
} from "../../../API  Functions/ProfileFunctions";
import Edit from "../../../assets/Edit.svg";
import deleteicon from "../../../assets/Order/Delete.svg";

// Toggle Switch Component
const ToggleSwitch = ({ isChecked, onChange }) => {
  return (
    <label className="flex cursor-pointer select-none items-center">
      <div className="relative">
        <input
          type="checkbox"
          checked={isChecked}
          onChange={onChange}
          className="sr-only"
        />
        <div
          className={`box block h-8 w-14 rounded-full ${
            isChecked ? "bg-green-500" : "bg-red-500"
          }`}
        ></div>
        <div
          className={`absolute left-1 top-1 flex h-6 w-6 items-center justify-center rounded-full bg-white transition ${
            isChecked ? "translate-x-full" : ""
          }`}
        ></div>
      </div>
    </label>
  );
};

const AllSubStoreTable = ({
  loading,
  additionalData = [],
  totalCount = 0,
  pageIndex,
  pageSize,
  onPageChange,
  onPageSizeChange,
  onToggleStatus,
  handleDelete,
  handleEdit,
}) => {
  const { t, i18n } = useTranslation();
  const [flag, setFlag] = useState(false);

  // Columns with Active/Inactive toggle switch and Delete/Edit icons
  const columns = useMemo(
    () => [
      {
        Header: t("Branch Name"),
        accessor: "store_name",
        Cell: ({ row }) => (
          <div className="flex items-center gap-2 text-start">
            <span>
              {i18n.language === "ar"
                ? row.original.store_name_ar
                : row.original.store_name}
            </span>
          </div>
        ),
      },
      {
        Header: t("Phone Number"),
        accessor: "store_phone",
        Cell: ({ row }) => (
          <div className="text-start">{row.original.store_phone}</div>
        ),
      },
      {
        Header: t("Email"),
        accessor: "store_email",
        Cell: ({ row }) => (
          <div className="text-start">{row.original.store_email}</div>
        ),
      },
      {
        Header: t("Branch Number"),
        accessor: "store_no",
        Cell: ({ row }) => (
          <div className="text-start">{row.original.store_no}</div>
        ),
      },
      {
        Header: t("Active/Inactive"),
        disableSortBy: true,
        accessor: "is_active",
        Cell: ({ row }) => {
          const subStoreId = row.original.store_id;
          const isActive = row.original.active;

          const handleToggleStatus = () => {
            onToggleStatus(subStoreId, isActive); // Call the parent function
          };

          return (
            <label className="flex cursor-pointer select-none items-center">
              <div className="relative">
                <input
                  type="checkbox"
                  checked={isActive}
                  onChange={handleToggleStatus}
                  className="sr-only"
                />
                <div
                  className={`box block h-6 w-12 rounded-full ${
                    isActive ? "bg-green-500" : "bg-gray-500"
                  }`}
                ></div>
                <div
                  className={`absolute left-1 top-1 flex h-4 w-5 items-center justify-center rounded-full bg-white transition ${
                    isActive ? "translate-x-full" : ""
                  }`}
                ></div>
              </div>
            </label>
          );
        },
      },
      {
        Header: t("Actions"),
        disableSortBy: true,
        accessor: "actions",
        Cell: ({ row }) => (
          <div className="flex gap-2">
            <div className="flex justify-center">
              <img
                src={Edit}
                style={{
                  cursor: "pointer",
                  height: "0.9rem",
                  width: "0.9rem",
                }}
                alt="Edit"
                className={`mt--2 ${i18n.language == "en" ? "mr-2" : "ml-2"}`}
                onClick={() => handleEdit(row.original?.store_id)}
              />
            </div>
            <img
              className="mx-4 h-4 w-4 cursor-pointer"
              src={deleteicon}
              alt="deleteicon"
              onClick={() => handleDelete(row.original?.store_id)}
            />
          </div>
        ),
      },
      // {
      //     Header: t("Actions"),
      //     accessor: "actions",
      //     Cell: ({ row }) => {
      //         const subStoreId = row.original.sub_store_id;

      //         const handleDelete = async () => {
      //             try {
      //                 await deleteSubStore(subStoreId); // Delete API
      //                 message.success(t("Sub-Store deleted successfully"));
      //             } catch (error) {
      //                 message.error(t("Error deleting sub-store"));
      //             }
      //         };

      //         const handleEdit = () => {
      //             // Handle the edit logic here (you may want to navigate to an edit page)
      //             message.info(t("Edit Sub-Store"));
      //         };

      //         return (
      //             <div className="flex gap-3">
      //                 <button
      //                     onClick={handleEdit}
      //                     className="py-1 px-3 rounded bg-blue-500 text-black"
      //                 >
      //                     <EditOutlined />
      //                 </button>
      //                 <button
      //                     onClick={handleDelete}
      //                     className="py-1 px-3 rounded bg-red-600 text-black"
      //                 >
      //                     <DeleteOutlined />
      //                 </button>
      //             </div>
      //         );
      //     },
      // },
    ],
    [t, i18n.language],
  );

  const { headerGroups, prepareRow, rows, getTableBodyProps } = useTable(
    {
      columns,
      data: additionalData,
    },
    useSortBy,
    usePagination,
    useRowSelect,
  );

  useEffect(() => {
    if (additionalData?.length === 0 && !flag) {
      const timer = setTimeout(() => {
        setFlag(true);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [additionalData, flag]);

  return (
    <div className="container flex flex-col mx-auto mt-4 w-full">
      {loading || (additionalData?.length === 0 && !flag) ? (
        <div className="h-screen w-[90%] mt-2">
          <ShimmerLoader />
        </div>
      ) : (
        <>
          {/* ✅ Horizontal scroll wrapper */}
          <div className="w-full overflow-x-auto">
            <table className="min-w-[900px] w-full font-normal border-collapse">
              {/* ✅ HEADER */}
              <thead>
                {headerGroups.map((headerGroup) => (
                  <tr
                    {...headerGroup.getHeaderGroupProps()}
                    className="bg-[#78CFEC66] text-sm font-semibold"
                  >
                    {headerGroup.headers.map((column) => (
                      <th
                        {...column.getHeaderProps(
                          column.getSortByToggleProps(),
                        )}
                        className="py-3 px-3 text-left whitespace-nowrap"
                      >
                        <div className="flex items-center gap-1">
                          {column.render("Header")}

                          {column.canSort && (
                            <span>
                              {column.isSorted ? (
                                column.isSortedDesc ? (
                                  <img
                                    src={Vectorsorting}
                                    className="min-h-[10px] min-w-[10px]"
                                    alt="desc"
                                  />
                                ) : (
                                  <img
                                    className="rotate-180 min-h-[10px] min-w-[10px]"
                                    src={Vectorsorting}
                                    alt="asc"
                                  />
                                )
                              ) : (
                                <img
                                  src={Vectorsorting}
                                  alt="sortable"
                                  className="min-h-[10px] min-w-[10px] opacity-30"
                                />
                              )}
                            </span>
                          )}
                        </div>
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>

              {/* ✅ BODY */}
              <tbody {...getTableBodyProps()} className="bg-[#F7F9FD] text-sm">
                {rows.map((row) => {
                  prepareRow(row);
                  return (
                    <tr
                      {...row.getRowProps()}
                      className="hover:bg-white transition duration-200 border-b border-gray-300 cursor-pointer"
                    >
                      {row.cells.map((cell, index) => (
                        <td
                          key={index}
                          {...cell.getCellProps()}
                          className="p-3 text-[#272728] whitespace-nowrap"
                        >
                          {cell.render("Cell")}
                        </td>
                      ))}
                    </tr>
                  );
                })}

                {rows.length === 0 && (
                  <tr>
                    <td colSpan={100}>
                      <Empty
                        className="mt-10"
                        description={t("No data available")}
                      />
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* ✅ Pagination */}
          <CustomPagination
            data={totalCount}
            pageIndex={pageIndex}
            pageCount={Math.ceil(totalCount / pageSize)}
            canPreviousPage={pageIndex > 1}
            canNextPage={pageIndex < Math.ceil(totalCount / pageSize)}
            previousPage={() => onPageChange(pageIndex - 1)}
            nextPage={() => onPageChange(pageIndex + 1)}
            gotoPage={(page) => onPageChange(page)}
            pageSize={pageSize}
            setPageSize={(size) => onPageSizeChange(size)}
          />
        </>
      )}
    </div>
  );
};

export default AllSubStoreTable;
